import React, { useState, useEffect } from 'react';
import { fhirWebhookApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Webhook, Plus, Trash2, Play, CheckCircle, XCircle, Clock,
  Activity, RefreshCw, ChevronRight, AlertTriangle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const EVENT_TYPES = [
  'Patient.create', 'Patient.update', 'Patient.delete',
  'Observation.create', 'Observation.update',
  'MedicationRequest.create', 'MedicationRequest.update',
  'Appointment.create', 'Appointment.update', 'Appointment.cancel',
  'DiagnosticReport.create', 'Encounter.create'
];

export default function FHIRWebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedHook, setSelectedHook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [form, setForm] = useState({ url: '', events: [], description: '', active: true });
  const [testing, setTesting] = useState(null);

  useEffect(() => { loadWebhooks(); }, []);

  const loadWebhooks = async () => {
    try {
      const res = await fhirWebhookApi.list();
      setWebhooks(res.data.webhooks || []);
    } catch (err) {
      console.error('Failed to load webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const createWebhook = async () => {
    if (!form.url || form.events.length === 0) {
      toast.error('URL and at least one event required');
      return;
    }
    try {
      const res = await fhirWebhookApi.register(form);
      toast.success(`Webhook created. Secret: ${res.data.secret?.substring(0, 12)}...`);
      setShowCreate(false);
      setForm({ url: '', events: [], description: '', active: true });
      loadWebhooks();
    } catch (err) {
      toast.error('Failed to create webhook');
    }
  };

  const deleteWebhook = async (id) => {
    try {
      await fhirWebhookApi.remove(id);
      toast.success('Webhook deleted');
      loadWebhooks();
      if (selectedHook?.id === id) setSelectedHook(null);
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const testWebhook = async (id) => {
    setTesting(id);
    try {
      await fhirWebhookApi.test(id);
      toast.success('Test payload delivered');
      loadWebhooks();
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const viewDeliveries = async (hook) => {
    setSelectedHook(hook);
    try {
      const res = await fhirWebhookApi.deliveries(hook.id);
      setDeliveries(res.data.deliveries || []);
    } catch (err) {
      setDeliveries([]);
    }
  };

  const toggleEvent = (event) => {
    setForm(prev => ({
      ...prev,
      events: prev.events.includes(event) ? prev.events.filter(e => e !== event) : [...prev.events, event]
    }));
  };

  return (
    <div className="space-y-6" data-testid="fhir-webhooks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            FHIR Webhooks
          </h1>
          <p className="text-[#64748B] mt-1">Real-time EHR event subscriptions</p>
        </div>
        <Button className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => setShowCreate(true)} data-testid="create-webhook-btn">
          <Plus className="w-4 h-4 mr-2" /> New Webhook
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Webhook List */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
          ) : webhooks.length > 0 ? (
            webhooks.map(hook => (
              <Card key={hook.id} className={`careiq-card hover:shadow-md transition-shadow ${selectedHook?.id === hook.id ? 'border-[#0E7490] border-2' : ''}`} data-testid={`webhook-${hook.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => viewDeliveries(hook)}>
                      <div className="flex items-center gap-2">
                        <Webhook className="w-4 h-4 text-[#0E7490]" />
                        <span className="font-medium text-[#0A2342] text-sm truncate max-w-[300px]">{hook.url}</span>
                        {hook.active ? (
                          <span className="px-2 py-0.5 text-[10px] bg-[#166534]/10 text-[#166534] rounded-full">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] bg-[#991B1B]/10 text-[#991B1B] rounded-full">Inactive</span>
                        )}
                      </div>
                      {hook.description && <p className="text-xs text-[#64748B] mt-1">{hook.description}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {hook.events.map((ev, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] bg-[#0E7490]/10 text-[#0E7490] rounded">{ev}</span>
                        ))}
                      </div>
                      <div className="flex gap-4 mt-2 text-[10px] text-[#64748B]">
                        <span>Delivered: {hook.delivery_count}</span>
                        <span>Failed: {hook.failure_count}</span>
                        {hook.last_triggered && <span>Last: {new Date(hook.last_triggered).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => testWebhook(hook.id)} disabled={testing === hook.id} data-testid={`test-webhook-${hook.id}`}>
                        {testing === hook.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteWebhook(hook.id)} data-testid={`delete-webhook-${hook.id}`}>
                        <Trash2 className="w-3 h-3 text-[#991B1B]" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-16 text-[#64748B]">
              <Webhook className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p className="text-sm">No webhooks configured</p>
              <p className="text-xs mt-1">Create a webhook to receive real-time FHIR events</p>
            </div>
          )}
        </div>

        {/* Delivery History */}
        <div>
          <Card className="careiq-card sticky top-24">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-[#64748B]">
                {selectedHook ? `Deliveries - ${selectedHook.url.substring(0, 30)}...` : 'Select a webhook'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedHook ? (
                deliveries.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-auto">
                    {deliveries.map(d => (
                      <div key={d.id} className="flex items-center gap-2 p-2 bg-[#F8FAFC] rounded">
                        {d.status === 'delivered' ? <CheckCircle className="w-3 h-3 text-[#166534]" /> : <XCircle className="w-3 h-3 text-[#991B1B]" />}
                        <div className="flex-1">
                          <div className="text-xs font-medium text-[#0A2342]">{d.event}</div>
                          <div className="text-[10px] text-[#64748B]">{new Date(d.delivered_at).toLocaleString()}</div>
                        </div>
                        <span className={`text-[10px] ${d.response_code === 200 ? 'text-[#166534]' : 'text-[#991B1B]'}`}>{d.response_code}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#64748B] text-center py-4">No deliveries yet</p>
                )
              ) : (
                <p className="text-xs text-[#64748B] text-center py-4">Click a webhook to view delivery history</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" aria-describedby="webhook-desc">
          <DialogHeader>
            <DialogTitle>Register FHIR Webhook</DialogTitle>
            <p id="webhook-desc" className="text-sm text-[#64748B]">Subscribe to real-time FHIR resource events</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Endpoint URL</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://your-app.com/fhir/webhook" className="mt-1" data-testid="webhook-url-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" className="mt-1" />
            </div>
            <div>
              <Label className="mb-2 block">Event Types</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-auto">
                {EVENT_TYPES.map(ev => (
                  <button key={ev} onClick={() => toggleEvent(ev)} className={`p-2 rounded text-left text-xs transition-all ${form.events.includes(ev) ? 'bg-[#0E7490]/10 text-[#0E7490] border border-[#0E7490]' : 'bg-[#F8FAFC] text-[#64748B] border border-[#E2E8F0] hover:bg-[#F1F5F9]'}`} data-testid={`event-${ev}`}>
                    {ev}
                  </button>
                ))}
              </div>
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={createWebhook} data-testid="register-webhook-btn">
              Register Webhook
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
